import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import BadgeDTO from "../dto/BadgeDTO";
import ProjectPositionDTO from "../dto/ProjectPositionDTO";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import { useFeatureToggle } from "../external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import AsyncAuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AsyncAuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import badgeService from "../services/BadgeService";
import OpsUserService from "../services/OpsUserService";
import { QueryKeyFactory } from "../services/queryKeys";
import TimeoffService from "../services/TimeoffService";
import UserService from "../services/UserService";
import { Routes } from "../types/Routes";
import { Features } from "../utils/Features";
import { unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import featureToggleList from "./../utils/toggleConfig.json";

const SHOW_CARDS = true;
const userKeys = new QueryKeyFactory(UserService.path);
const badgeKeys = new QueryKeyFactory(badgeService.path);
const timeoffKeys = new QueryKeyFactory(TimeoffService.path);
const NO_POSITIONS: ProjectPositionDTO[] = [];
const NO_BADGES: BadgeDTO[] = [];
const EMPTY_BADGE_PAGE: PagedResult<BadgeDTO> = { content: [], pageNumber: 0, totalElements: 0 };

const usePersonalInfo = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { paramExternalId } = useParams<{ paramExternalId?: string }>();
  const coreUserId = AuthService.getUser()?.externalId;
  const openedAsSelf = useRef(!paramExternalId);
  const canonicalSelfId = useRef<string | undefined>(undefined);
  const [recoverSelf, setRecoverSelf] = useState(false);
  const [manualLoading, setIsLoading] = useState(false);
  const [userUpdate, setUserUpdate] = useState<EnsolversOpsUser | undefined>(undefined);
  const [userAvatarUpdate, setUserAvatarUpdate] = useState<EnsolversOpsUser | undefined>(undefined);
  const [userAvatarRemove, setUserAvatarRemove] = useState<EnsolversOpsUser | undefined>(undefined);
  const [isVacationReportEnabled] = useFeatureToggle(featureToggleList, Features.VACATION_REPORT);
  const [isCertificationListEnabled] = useFeatureToggle(featureToggleList, Features.CERTIFICATIONS);
  const showingOwnProfile = recoverSelf || (openedAsSelf.current && (!paramExternalId || paramExternalId === canonicalSelfId.current));
  const userQueryKey = showingOwnProfile
    ? userKeys.detail(`core:${coreUserId ?? "anonymous"}`)
    : userKeys.detail(paramExternalId ?? "pending");

  const userQuery = useQuery({
    queryKey: userQueryKey,
    queryFn: () =>
      showingOwnProfile || !paramExternalId
        ? unwrap(UserService.findByCoreUserId(coreUserId!))
        : unwrap(UserService.getByExternalId(paramExternalId)),
    enabled: showingOwnProfile ? !!coreUserId : !!paramExternalId,
    staleTime: TABLE_QUERY_STALE_TIME,
    retry: (failureCount, error) => {
      const status = error instanceof ApiError ? error.apiResponse.getHttpStatusCode() : undefined;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
  const user = userQuery.data;
  const opsUserId = user?.externalId;

  const ownerQuery = useQuery({
    queryKey: userKeys.detail(`owner:${paramExternalId ?? "self"}`),
    queryFn: () => AsyncAuthService.getUser(),
    enabled: !!paramExternalId && !showingOwnProfile,
  });
  const isProfileOwner = showingOwnProfile || ownerQuery.data?.externalId === paramExternalId;

  const positionsQuery = useQuery({
    queryKey: userKeys.detail(`positions:${paramExternalId || opsUserId || ""}`),
    queryFn: () => unwrap(UserService.getProjectPositions((paramExternalId || opsUserId)!)),
    enabled: !!opsUserId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const badgesQuery = useQuery({
    queryKey: badgeKeys.list({ scope: "ops-user", userId: opsUserId ?? "", page: 0, pageSize: 100 }),
    queryFn: () => unwrap(badgeService.getBadgesPagedForOpsUser(0, 100, opsUserId!)),
    enabled: SHOW_CARDS && !!isCertificationListEnabled && !!opsUserId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const vacationQuery = useQuery({
    queryKey: timeoffKeys.detail(`balance:${opsUserId ?? ""}`),
    queryFn: () => unwrap(TimeoffService.getVacationBalance(opsUserId!, new Date().getFullYear(), new Date().getMonth() + 1)),
    enabled: !!isVacationReportEnabled && !!opsUserId && !!user?.vacationsComputedSince,
  });

  const refreshUser = () => queryClient.invalidateQueries({ queryKey: userQueryKey });

  const updateMutation = useMutation({
    mutationFn: (nextUser: EnsolversOpsUser) => OpsUserService.update(nextUser),
    onMutate: async (nextUser) => {
      await queryClient.cancelQueries({ queryKey: userQueryKey });
      const previous = queryClient.getQueryData<EnsolversOpsUser>(userQueryKey);
      queryClient.setQueryData(userQueryKey, previous ? { ...previous, ...nextUser } : nextUser);
      return { previous };
    },
    onError: (_error, _nextUser, context) => {
      if (context) {
        queryClient.setQueryData(userQueryKey, context.previous);
      }
    },
    onSettled: async (_saved, error, nextUser) => {
      const refreshes = [queryClient.invalidateQueries({ queryKey: userQueryKey })];
      if (!error && nextUser.externalId) {
        refreshes.push(queryClient.invalidateQueries({ queryKey: timeoffKeys.detail(`balance:${nextUser.externalId}`) }));
      }
      await Promise.all(refreshes);
    },
  });

  const selfDrataMutation = useMutation({
    mutationFn: () => unwrap(UserService.regenerateDrataIdentifier()),
    onSuccess: () => refreshUser(),
  });

  const adminDrataMutation = useMutation({
    mutationFn: (externalId: string) => unwrap(UserService.regenerateDrataIdentifierByAdmin(externalId)),
    onSuccess: () => refreshUser(),
  });

  useEffect(() => {
    if (!opsUserId || paramExternalId == opsUserId) return;
    if (openedAsSelf.current) canonicalSelfId.current = opsUserId;
    history.replace(`${Routes.PROFILE}/${opsUserId}`);
  }, [opsUserId, paramExternalId, history]);

  useEffect(() => {
    if (!userQuery.isError) return;
    if (showingOwnProfile) {
      history.replace(Routes.DASHBOARD);
      return;
    }
    setRecoverSelf(true);
    history.replace(Routes.PROFILE);
  }, [userQuery.isError, showingOwnProfile, history]);

  const getBadgesPagedForUser = async (page?: number, pageSize?: number): Promise<PagedResult<BadgeDTO>> => {
    if (!opsUserId) return EMPTY_BADGE_PAGE;
    const nextPage = page ?? 0;
    const nextSize = pageSize ?? 100;
    return queryClient.fetchQuery({
      queryKey: badgeKeys.list({ scope: "ops-user", userId: opsUserId, page: nextPage, pageSize: nextSize }),
      queryFn: () => unwrap(badgeService.getBadgesPagedForOpsUser(nextPage, nextSize, opsUserId)),
      staleTime: TABLE_QUERY_STALE_TIME,
    });
  };

  const getUserDataSize = (instance: EnsolversOpsUser) => {
    const additionalVacationsField = instance.additionalVacationsAmount && instance.additionalVacationsAmount > 0 ? 1 : 0;
    const initialTakenVacationsField = instance.initialTakenVacations && instance.initialTakenVacations > 0 ? 1 : 0;
    const vacationsComputedDateField = instance.vacationsComputedSince ? 1 : 0;
    return additionalVacationsField + initialTakenVacationsField + vacationsComputedDateField;
  };

  const setUser = (nextUser: EnsolversOpsUser | undefined) => {
    queryClient.setQueryData(userQueryKey, nextUser);
  };

  const handleOnRemoveAvatar = async () => {
    if (!userAvatarRemove?.avatarUrl) return;
    const cleared = { ...userAvatarRemove, avatarUrl: "" };
    try {
      await updateMutation.mutateAsync(cleared);
      NotificationService.info("Avatar removed!");
    } catch {
      // QueryClient already toasts ApiError from the failed update.
    } finally {
      setUserAvatarRemove(undefined);
    }
  };

  const handleOnClickRemoveAvatarButton = () => {
    if (user?.avatarUrl) {
      setUserAvatarRemove(user);
    } else {
      NotificationService.error("No avatar to remove!");
    }
  };

  const validUser = (ensolversOpsUser: EnsolversOpsUser): string => {
    if (new Date(ensolversOpsUser.vacationsComputedSince!) < new Date(ensolversOpsUser.shortStart)) {
      return "Vacations Computed Since must be equal or greater than Start Date";
    }
    if (ensolversOpsUser.vacationAccumulation! < 0 || ensolversOpsUser.vacationAccumulation! > 30) {
      return "Vacations accumulation per year should be positive and less or equal than 30 days";
    }
    return "OK";
  };

  const onSave = async (ensolversOpsUser: EnsolversOpsUser) => {
    const userValidationMessage = validUser(ensolversOpsUser);
    if (userValidationMessage !== "OK") {
      setUserUpdate(undefined);
      NotificationService.error(userValidationMessage);
      return;
    }
    try {
      await updateMutation.mutateAsync(ensolversOpsUser);
      NotificationService.info("User updated!");
    } catch {
      // QueryClient already toasts ApiError from the failed update.
    } finally {
      setUserUpdate(undefined);
    }
  };

  const clearUserUpdate = () => setUserUpdate(undefined);

  const showAdditionalVacationAmount = () => {
    return user?.additionalVacationsAmount! > 0 || user?.shortStart != user?.vacationsComputedSince;
  };

  const selfRegenerateDrataIdentifier = async () => {
    try {
      await selfDrataMutation.mutateAsync();
    } catch {
      // QueryClient already toasts ApiError from the failed regenerate.
    }
  };

  const regenerateDrataIdentifierByAdmin = async (ensolversOpsUserExternalId: string) => {
    try {
      await adminDrataMutation.mutateAsync(ensolversOpsUserExternalId);
    } catch {
      // QueryClient already toasts ApiError from the failed regenerate.
    }
  };

  const badges = useMemo(() => badgesQuery.data?.content ?? NO_BADGES, [badgesQuery.data]);

  return {
    history,
    user,
    setUser,
    isLoading: userQuery.isLoading || manualLoading,
    isProfileOwner,
    canEdit: RoleUtils.isAdmin(),
    handleOnClickRemoveAvatarButton,
    badges,
    projectPositions: positionsQuery.data ?? NO_POSITIONS,
    userAvatarUpdate,
    setUserAvatarUpdate,
    userUpdate,
    setUserUpdate,
    setIsLoading,
    userAvatarRemove,
    setUserAvatarRemove,
    handleOnRemoveAvatar,
    onSave,
    clearUserUpdate,
    showAdditionalVacationAmount,
    vacationReport: vacationQuery.data,
    getUserDataSize,
    isRequestPending: updateMutation.isPending,
    getBadgesPagedForUser,
    SHOW_CARDS,
    isCertificationListEnabled,
    updateCurrentHistoryEntry: (externalId: string) => {
      if (paramExternalId != externalId) {
        history.replace(`${Routes.PROFILE}/${externalId}`);
      }
    },
    paramExternalId,
    regenerateDrataIdentifierByAdmin,
    selfRegenerateDrataIdentifier,
  };
};

export default usePersonalInfo;
export type UsePersonalInfoReturnType = ReturnType<typeof usePersonalInfo>;
